// Logged-in Billing route ("Gems & plan"). Keep keys in sync with pl/appBilling.ts.
// Money, prices, pools and retention days live in the API and reach the component —
// they are never typed into a locale. Currency UI = "tokens" (rule 34, round B-R3,
// 20.09); API field and key names (`gem_costs`, `gems`) are technical addresses and stay.
//
// 0018 (design 02, states 3–7 and 12). Billing does NOT repeat the catalogue (PM
// decision 1): plans and packs live on the pricing page; this screen has the balance,
// "where they went", the plan, invoices and ONE primary action that depends on the state.
export const appBilling = {
  title: 'Tokens and plan',
  subtitle: 'Balance, plan and payments.',
  details: {
    balance: 'Token sources and expiry',
    plan: 'Limits and storage',
  },
  state: {
    loading: 'Loading your account…',
    errorTitle: 'We could not load your balance and plan.',
    retry: 'Try again',
  },
  invStatus: {
    paid: 'paid',
    pending: 'pending',
    failed: 'failed',
    refunded: 'refunded',
  },
  // Balance headline: "12 = 4 edits · an edit costs 3 gems". `edits` and `gems` arrive
  // ALREADY inflected and formatted, derived from the server's price (lib/gems.ts).
  hero: {
    equals: '= {edits} · an edit costs {gems}',
  },
  plan: {
    heading: 'Your plan',
    headingNamed: '{name} plan',
    statusRow: 'Status',
    accessUntilShort: 'Access until',
    label: 'Plan',
    noPlan: 'No plan',
    whatPlanGives: 'What a plan gives you',
    whatPlanGivesBody: 'a pool of gems every month + access to publishing',
    catalogNotePre: 'Plans and packs are on the ',
    catalogLink: 'Plans and packs',
    catalogNotePost: ' - this screen is only your account.',
    perMonth: '/ mo',
    price: 'Price',
    accountsLink: 'Your accounts',
    statusActive: 'active',
    statusCancelled: 'cancelled',
    nextCharge: 'Next charge',
    accessUntil: 'Access and plan gems until',
    slots: 'Publishing accounts',
    slotsOf: '{used} / {total} {unit}',
    slotsUnit_one: 'slot',
    slotsUnit_few: 'slots',
    slotsUnit_many: 'slots',
    storage: 'Storage for your footage',
    storageOf: '{used} / {total} GB',
    readyEdits: 'Finished edits',
    readyEditsValue: 'downloadable for {days} days from the file’s creation',
    uploads: 'Tracks and your own clips',
    // No clock (Domi, 2026-09-15): a user's material never disappears on its own.
    uploadsValue: 'stay on your account',
    boughtGems: 'Purchased gems',
    boughtGemsValue: 'stay until the end of their own validity',
  },
  storage: {
    retention: 'Finished edits stay downloadable for {days} days from the file’s creation.',
  },
  cancel: {
    body: 'You cancel in the customer portal (“Manage payment method” → “Cancel subscription”), without writing to us. Access and plan gems stay until the end of the paid period.',
  },
  invoice: {
    subscription: 'subscription',
    topup: 'tokens',
  },
  notice: {
    successPlanTitle: 'Payment received - the {name} plan is active.',
    successTopupTitle: 'Payment received - the tokens are now in your account.',
    successConfirmed: 'The {gems} grant is confirmed in your account history. The balance and invoices below are current.',
    pendingTitle: 'Payment is waiting for confirmation',
    checkingSub: 'We are checking the payment and gem grant. Do not pay again.',
    pendingSub: 'Stripe returned you to EDIMI, but the webhook that grants the gems has not arrived yet. Do not pay again - check again in a moment.',
    unavailableTitle: 'We could not confirm the payment yet',
    unavailableSub: 'We do not show success until both the payment and the gem grant are confirmed on our side.',
    unverifiedTitle: 'We cannot link this return to a payment',
    pendingPill: 'payment awaiting confirmation',
    unavailablePill: 'we could not confirm the payment',
    unverifiedPill: 'returned without payment confirmation',
    unverifiedSub: 'The page address alone does not prove a payment. Your current balance and invoices are shown below.',
    checkAgain: 'Check again',
    invoicePdf: 'Download invoice PDF',
    invoicePdfNamed: 'Download {number} - PDF',
    cancelTitle: 'Payment cancelled - account unchanged.',
    cancelSub: 'Checkout was not completed and nothing was charged. You can return to the order and choose a payment method in Stripe.',
    backToOrder: 'Return to the order',
    successTitle: 'Payment received',
    successSub: 'Your account balance will update shortly.',
    declinedTitle: 'Payment declined / cancelled',
    declinedSub: 'The transaction was not completed - your account is unchanged. Try again or choose a different payment method.',
  },
  // THE TWO-BUCKET METER. Plan gems and purchased gems expire under DIFFERENT rules,
  // so a single balance number lies. The split and the spend order are mandatory.
  meter: {
    total: 'Your gems',
    pillZero: 'no tokens',
    fromPlan: 'From your plan',
    fromPlanOf: '{used} / {total}',
    renewsOn: 'Renews {date}',
    choosePlan: 'Choose a plan',
    renewsOnPlus: 'Renews {date} · +{count} gems',
    accessUntil: 'Access until {date}',
    purchased: 'Purchased and bonus',
    purchasedNote: 'Purchased gems are valid for 24 months from purchase; the starter bonus never expires.',
    spendOrder: 'We spend plan gems first, then purchased gems - starting with those closest to expiry; the starter bonus goes last.',
    noPlanHint: 'You have no plan - only purchased gems and your starter bonus.',
  },
  actions: {
    topup: 'Top up tokens',
    topupShort: 'Top up',
    resumeShort: 'Resume',
    changePlan: 'Change plan',
    resume: 'Resume the {name} plan',
    contact: 'Contact us',
    manage: 'Manage payment method',
    disabledHint: 'Payments are temporarily unavailable on our side.',
  },
  // Rule 35 took two sentences out of here: `rule` ("Low balance starts below…")
  // and `renewal` ("The renewal on … adds …"). The threshold is shown by the
  // COLOUR of the balance and its pill, and the renewal date is one mono line on
  // the plan card — an explanation of a control the reader is already looking at
  // is exactly what principle 8 forbids.
  low: {
    title: 'Low balance: {gems} = {edits}.',
    noPlan: 'A gem pack or a plan with a monthly pool will help.',
  },
  zero: {
    title: '0 gems = 0 edits. Rendering cannot start.',
    body: 'Two ways forward: buy a pack (gems right away, valid for 24 months) or a higher plan from the next period.',
    bodyNoPlan: 'Two ways forward: buy a pack (gems right away, valid for 24 months) or a plan with a monthly pool of gems.',
    refunded: 'Failed renders cost nothing - returned automatically: {gems}.',
  },
  cancelled: {
    title: 'The {name} plan is cancelled. Access until {date}.',
    body: 'Until that date your plan gems work as before. Every finished edit stays downloadable for {renderDays} days from its creation - that clock does not depend on the plan. Uploaded tracks and your own clips stay on your account. Purchased gems stay until the end of their own validity.',
  },
  gate3: {
    title: 'Purchases are unavailable right now',
    body: 'Nothing was charged. Your current gems and the rest of your account work as usual.',
    bodyPlans: 'Plans are unavailable right now. Packs and the rest of your account work as usual.',
    bodyPacks: 'Gem packs are unavailable right now. Plans and the rest of your account work as usual.',
  },
  // USAGE (rule 35) — the day-by-day chart module inside the balance card.
  // The figures come from `token_ledger` (usage_breakdown()), never from
  // subtracting balances.
  //
  // What DIED with the "Gem activity" list dialog: `period`, `summary`,
  // `spent_render` / `spent_publish` / `spent_cut` (a unit price after the fact
  // explained the very number the reader is looking at — principle 8) and
  // `sourceNote`. What is left: three layer names, two figure labels and the
  // chart's name for a screen reader.
  usage: {
    heading: 'Usage',
    spentLabel: 'Used',
    refundedLabel: 'Refunded',
    render: 'Edits',
    publish: 'Posts',
    cut: 'Cuts',
    layerMusic: 'Music',
    textAnalysis: 'Lyric analysis',
    refundTextAnalysis: 'Returned · lyric analysis',
    // The module's empty state: a glyph and a title, no second sentence.
    emptyTitle: 'No usage yet',
    // The image's name and the data table's caption — the chart is a picture, so
    // a day's numbers must be readable without aiming at a ten-pixel bar.
    chartLabel: 'Token usage day by day',
    dayColumn: 'Day',
  },
  payment: {
    manage: 'Manage payment method',
    nextChargeLabel: 'Next charge:',
  },
  invoices: {
    heading: 'Payments and invoices',
    loading: 'Loading invoices…',
    empty: 'No payments yet',
    allInPortal: 'All of them in the customer portal',
    lineCount_one: '{count} line',
    lineCount_few: '{count} lines',
    lineCount_many: '{count} lines',
    lineProration: '{plan} - prorated from {date}',
    lineRefund: '{plan} - refund for the unused period',
    lineSlots: 'Extra slots: {count}',
    linePlan: '{plan} plan - {month}',
    renewal: 'Subscription renewal',
    pdf: 'Download PDF',
  },
}
